/**
 * Cross-filter by click (D528; research doc 05 §4 point 10): the ONE contract between a chart and the filter state of its screen.
 *
 * A chart that can be clicked calls `onSelect(dimension, value, { additive })` - a click on a bar, a slice, a column, a heat-map cell. The
 * screen owns what happens: it applies the selection to its filter state with {@link applyCrossFilter} and shows the new condition as a removable
 * chip. Every chart type reaches the filter the same way, so D529's new chart types call exactly the same function the existing ones do.
 *
 *  - a plain click on a value that is not selected FILTERS to that value; on the only value selected, it takes the filter off again;
 *  - a Shift-click (or Ctrl/Cmd-click) ADDS the value to the ones selected, or takes it out when it is already there;
 *  - a person (a customer, a driver, a courier, an order) is never cross-filtered: a click on a person is a drill, and a filter that pins one
 *    is not something a link or a saved set may keep (`applyCrossFilter` leaves the state as it was and says why);
 *  - a value the dimension cannot take (the wrong shape for its type) leaves the state as it was.
 *
 * Pure: no I/O, no DOM. The result is the new state; the caller asks the server again (nothing is filtered on the client).
 */
import { getDimension, validFilterValue } from '@vello/analytics-semantic';
import { type BiFilterSpec, type BiFilterState, canAddCondition } from './bi-filters.js';

/** A chart's click. `additive` is a Shift (or Ctrl / Cmd) click. */
export interface CrossFilterOptions {
  readonly additive?: boolean;
}

/** The contract every clickable chart receives: dimension id, the key of the clicked value, the click's modifiers. */
export type CrossFilterSelect = (dimension: string, value: string, options?: CrossFilterOptions) => void;

/** What a chart needs to be clickable: which dimension its marks stand for, and where a click goes. */
export interface CrossFilterTarget {
  readonly dimension: string;
  readonly onSelect: CrossFilterSelect;
}

export type CrossFilterOutcome =
  | { readonly state: BiFilterState; readonly changed: true; readonly effect: 'added' | 'replaced' | 'extended' | 'narrowed' | 'cleared' }
  | { readonly state: BiFilterState; readonly changed: false; readonly reason: 'personal' | 'unknown_dimension' | 'invalid_value' | 'full' };

/** The click modifiers of a DOM event as the contract's options (Shift, or Ctrl / Cmd). */
export function additiveOf(event: { readonly shiftKey?: boolean; readonly ctrlKey?: boolean; readonly metaKey?: boolean }): CrossFilterOptions {
  return event.shiftKey === true || event.ctrlKey === true || event.metaKey === true ? { additive: true } : {};
}

const isInclusion = (f: BiFilterSpec): boolean => f.op === 'in' || f.op === 'eq';

/** Apply a chart click to the filter state. */
export function applyCrossFilter(state: BiFilterState, dimension: string, value: string, options: CrossFilterOptions = {}): CrossFilterOutcome {
  const def = getDimension(dimension);
  if (def === undefined || def.filterOnly === true) return { state, changed: false, reason: 'unknown_dimension' };
  if (def.personal) return { state, changed: false, reason: 'personal' };
  if (!validFilterValue(def, value)) return { state, changed: false, reason: 'invalid_value' };

  // The one inclusion condition of the AND list on this dimension (the server refuses two positive sets on one dimension).
  const at = state.filters.findIndex((f) => f.dimension === dimension && isInclusion(f));
  const current = at >= 0 ? (state.filters[at] as BiFilterSpec) : undefined;
  if (current === undefined) {
    if (!canAddCondition(state)) return { state, changed: false, reason: 'full' };
    return { state: { ...state, filters: [...state.filters, { dimension, op: 'in', values: [value] }] }, changed: true, effect: 'added' };
  }
  const has = current.values.includes(value);
  const replaceAt = (spec: BiFilterSpec | undefined): BiFilterState => ({
    ...state,
    filters: spec === undefined ? state.filters.filter((_, i) => i !== at) : state.filters.map((f, i) => (i === at ? spec : f)),
  });
  if (options.additive === true) {
    if (!has) return { state: replaceAt({ ...current, op: 'in', values: [...current.values, value] }), changed: true, effect: 'extended' };
    const rest = current.values.filter((v) => v !== value);
    return rest.length === 0 ? { state: replaceAt(undefined), changed: true, effect: 'cleared' } : { state: replaceAt({ ...current, op: 'in', values: rest }), changed: true, effect: 'narrowed' };
  }
  if (has && current.values.length === 1) return { state: replaceAt(undefined), changed: true, effect: 'cleared' };
  return { state: replaceAt({ ...current, op: 'in', values: [value] }), changed: true, effect: 'replaced' };
}

/** Is this value part of the filter right now? (A chart may mark its selected marks.) */
export function isSelected(state: BiFilterState, dimension: string, value: string): boolean {
  return state.filters.some((f) => f.dimension === dimension && isInclusion(f) && f.values.includes(value));
}
